import React, { useEffect, useRef } from 'react';
import { array, boolean, object, string } from 'yup';
import { extractHookFormErrors } from 'src/components/Form/Form.utils';
import { useRequestRevisionsStore } from 'src/features/revisions/use-revisions-context-provider';
import { useVideoProgressActions } from 'src/features/video-player/use-video-progress-store';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useRequestRevisionVideoPlayerGlobalStore } from 'src/features/revisions/use-request-revision-video-player-global-store';
import { shallow } from 'zustand/shallow';
import { yupResolver } from '@hookform/resolvers/yup';
import EmojiPicker from 'src/components/ui/emoji-picker';
import { Button } from 'src/components/ui/button/button';
import { Smile, UploadCloud } from 'lucide-react';
import { RevisionChatSelectedMedia } from './revision-chat-selected-media';
import { DropzonePopoverInput } from 'src/components/Form/S3UploadInput/hook-form/dropzone-popover-input';
import { useShallow } from 'zustand/react/shallow';
import { StoreCommentFormObject } from 'src/pages/RevisionsPage/revisions-content-main/revision-chat-content.types';
import { ButtonLoader } from 'src/components/ui/loader';
import { useBreakpoint } from 'src/lib/hooks';
import { useChatToast } from 'src/components/chat-toast/chat-toast';
import { useDecoratedRequestContext } from 'src/features/requests/use-decorated-request-context';
import { useRequestMutations } from 'src/features/requests/use-request-mutations';
import { useToast } from 'src/components/ui/use-toast';
import { useMediaUploadStore } from 'src/lib/services/media-upload-store';
import { ElapsedTimeBadge } from './elapsed-time-badge';
import { HelpMeWriteButton, HelpMeWritePanel } from 'src/features/ai-assist';
import {
  MentionTextarea,
  MentionTextareaHandle,
} from 'src/components/Form/mention-textarea';

type Props = {
  className?: string;
  children?: React.ReactNode;
  commentMutation: any;
  onSubmit?: () => void;
  /**
   * Set when the form is composing a reply. The revision travels with the id
   * deliberately: the backend files a reply under its parent's revision rather
   * than the one the player is parked on, so the form has to compare the two
   * before stamping a timestamp — and a caller that could supply the id without
   * the revision would silently disable the timestamp control instead.
   */
  replyTarget?: {
    commentId: string;
    revisionId: string;
  };
};

const validationSchema = object().shape({
  include_time: boolean(),
  media: array().nullable(),
  comment: string()
    .nullable()
    .when('media', {
      is: (media: any) => {
        return !media.length;
      },
      then: string().required('Field is required'),
    }),
});

const RevisionChatInputForm: React.FC<Props> = ({
  className,
  replyTarget,
  commentMutation,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const { isXs } = useBreakpoint();
  const { isToastActive, trigger } = useChatToast();
  // Both this form and the edit form expect a DecoratedRequestProvider in
  // the tree. RevisionsPage wraps in one; any future mount site needs the
  // same.
  const request = useDecoratedRequestContext();
  const requestMutations = useRequestMutations();
  const { toast } = useToast();

  const { selectedRevision, selectedRevisionMedia } = useRequestRevisionsStore();
  const mentionRef = useRef<MentionTextareaHandle | null>(null);
  const { status } = useMediaUploadStore(useShallow(({ status }) => ({ status })));
  const { isLatest = false } = selectedRevision ?? {};
  const [helpMeWriteOpen, setHelpMeWriteOpen] = React.useState(false);

  // A reply is filed under its parent comment's revision (see
  // RevisionCommentManager::reply), not the one the player is parked on — so
  // replying to an older version's comment while the latest one plays used to
  // stamp that version's file and playhead onto the reply, producing a
  // reference no viewer could ever resolve ("File not found", #2663). Only
  // carry playback context when the two revisions agree.
  const playbackMatchesTarget = !replyTarget || replyTarget.revisionId === selectedRevision?.id;

  // `isLatest` belongs in here rather than only in submit(): the two used to
  // disagree, so on a non-latest thread the checkbox rendered enabled with an
  // inviting tooltip and the timestamp was then dropped on the way out.
  const canIncludeTime =
    !!(selectedRevisionMedia?.isVideo || selectedRevisionMedia?.isAudio) &&
    playbackMatchesTarget &&
    isLatest;

  const defaultValues = {
    comment: '',
    include_time: canIncludeTime,
    media: [],
  };

  // :)
  const { getPlayedSeconds } = useVideoProgressActions();

  const pageStore = useRequestRevisionVideoPlayerGlobalStore(
    ({ timeFormat, pause }) => ({
      timeFormat,
      pause,
    }),
    shallow,
  );

  const form = useForm<StoreCommentFormObject>({
    defaultValues,
    /* eslint-disable */
    //@ts-ignore
    resolver: yupResolver(validationSchema),
  });

  // `disabled` on the badge stops further input, it does not clear the value.
  // Ticking the box on the latest version and then switching the player to an
  // older one mid-compose left a checked box and a ticking timecode on screen
  // while submit silently discarded the timestamp.
  useEffect(() => {
    if (!canIncludeTime) {
      form.setValue('include_time', false);
    }
  }, [canIncludeTime, form]);

  const includeTime = form.watch('include_time');
  const comment = form.watch('comment');
  const media = form.watch('media');

  const mediaUploading = (media ?? []).some((m) => status[m.id]?.isUploading);
  const uploadsFailed = (media ?? []).some((m) => status[m.id]?.failed);

  const submit = async (values: StoreCommentFormObject) => {
    try {
      await commentMutation.mutateAsync([
        replyTarget ? replyTarget.commentId : selectedRevision!.id,
        {
          ...values,
          revision_timestamp:
            canIncludeTime && values.include_time ? getPlayedSeconds() : undefined,
          media_id: playbackMatchesTarget ? selectedRevisionMedia?.id : undefined,
        },
      ]);

      // When re-edit flow is active (chat toast visible), fire the re-edit mutation
      // and then close trigger-mode chat toast on success. On error, show a regular toast
      // and keep the chat toast open.
      // Only call the API if re-edit hasn't been requested yet to avoid duplicate calls
      if (isToastActive && !request.requested_reedit_at) {
        try {
          await requestMutations.requestReedit.mutateAsync(request.id);
          trigger();
          // Show regular global toast for success
          toast({
            description: t('alerts:success.request_reedit', {
              defaultValue: 'Re-edit request was sent to your editor!',
            }),
          });
        } catch (err: any) {
          // Show regular global toast for error
          toast({
            title: t('alerts:error.request_reedit_failed', {
              defaultValue: 'Request re-edit failed',
            })!,
            description: t('alerts:error.generic_try_again', {
              defaultValue: 'Please try again.',
            })!,
            variant: 'destructive',
          });
        }
      } else if (isToastActive && request.requested_reedit_at) {
        // Re-edit already requested, just close the toast without calling API
        trigger();
      }
    } catch (e: any) {
      const errors = extractHookFormErrors(e) ?? {};

      console.log(errors);
      Object.keys(errors).forEach((key) => {
        form.setError(key as any, { type: 'custom', message: errors[key] });
      });
    } finally {
      form.reset();
      onSubmit?.();
    }
  };

  const handleEmojiSelect = (emoji: any) => {
    // Delegate to MentionTextarea so the caret-to-markup translation stays
    // in one place. Direct slicing of `comment` at textarea.selectionStart
    // would shred any preceding `@[Name](uuid)` token because the caret is
    // in plain-text coordinates while `comment` is the markup string.
    mentionRef.current?.insertAtCaret(emoji.native);
  };

  return (
    <div className={className}>
      <FormProvider {...form}>
        <RevisionChatSelectedMedia name={'media'} />
        <form onSubmit={form.handleSubmit(submit)}>
          <div>
            <Controller
              name={'comment'}
              control={form.control}
              render={({ field }) => (
                <MentionTextarea
                  ref={mentionRef}
                  requestId={request.id}
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  rows={isXs ? 1 : 2}
                  // During the re-edit flow the chat toast ("leave a comment
                  // for your editor") is up — isToastActive is only ever set by
                  // that flow — so prompt the client to spell out the changes
                  // they want before they hit Send (#2528).
                  placeholder={
                    isToastActive
                      ? t('Let your editor know what needs to be changed...')!
                      : t('Type a message...')!
                  }
                  onKeyDown={(e) => {
                    if (includeTime) {
                      pageStore.pause();
                    }

                    // react-mentions intercepts Enter/Up/Down/Escape when the
                    // suggestion list is open. Bail out so picking a mention
                    // with Enter doesn't also submit the form.
                    if (e.defaultPrevented) {
                      return;
                    }

                    if (e.shiftKey || e.code !== 'Enter') {
                      return;
                    }

                    if (
                      form.formState.isSubmitting ||
                      mediaUploading ||
                      uploadsFailed ||
                      commentMutation.isLoading
                    ) {
                      return;
                    }

                    e.preventDefault();

                    form.handleSubmit(submit)();
                  }}
                />
              )}
            />
          </div>
          <div className="tw-flex tw-flex-col tw-gap-2">
            {/* Single row at every width, Send inline on the right (#2484). On
                mobile "Help me write" collapses to icon-only (hideLabelOnMobile)
                so the actions + Send fit one row; the row doesn't wrap, and the
                inner action group stays `flex-wrap` + `min-w-0` as a safety net
                for the very narrowest phones (the timer would reflow, not Send).
                `items-start` keeps Send aligned to the top if that ever happens. */}
            <div className={'tw-mt-2 tw-flex tw-items-start tw-justify-between tw-gap-2'}>
              <div className={'tw-flex tw-min-w-0 tw-grow tw-flex-wrap tw-items-center tw-gap-2'}>
                <DropzonePopoverInput name={'media'} model={'request_revision_comment'}>
                  <Button type={'button'} variant={'outline'} size={'sm'}>
                    <UploadCloud size={18} />
                  </Button>
                </DropzonePopoverInput>
                <EmojiPicker onSelect={handleEmojiSelect}>
                  <Button type={'button'} variant={'outline'} size={'sm'}>
                    <Smile size={18} />
                  </Button>
                </EmojiPicker>

                <HelpMeWriteButton
                  size={'sm'}
                  hideLabelOnMobile
                  onOpen={() => setHelpMeWriteOpen(true)}
                />

                <ElapsedTimeBadge
                  title={
                    canIncludeTime
                      ? t('Check the box to include current playback time in the message!')!
                      : t(
                          'Playback time can only be attached to the current version of this thread',
                        )!
                  }
                  disabled={!canIncludeTime}
                  formattingOptions={{
                    format: pageStore.timeFormat,
                    fps: selectedRevisionMedia?.fps ?? 24,
                  }}
                />
              </div>

              <div className={'tw-flex tw-shrink-0 tw-gap-2 tw-ms-auto'}>
                <Button
                  variant={'brand'}
                  size={'sm'}
                  disabled={
                    form.formState.isSubmitting ||
                    mediaUploading ||
                    uploadsFailed ||
                    commentMutation.isLoading
                  }
                  type={'submit'}
                >
                  {!form.formState.isSubmitting && !commentMutation.isLoading && t('Send')}
                  <ButtonLoader
                    className={'tw-ms-0'}
                    loading={form.formState.isSubmitting || commentMutation.isLoading}
                  />
                </Button>
              </div>
            </div>
          </div>
        </form>
      </FormProvider>
      <HelpMeWritePanel
        open={helpMeWriteOpen}
        onOpenChange={setHelpMeWriteOpen}
        surface={'revision_chat_reply'}
        currentText={comment ?? ''}
        onInsert={(draft) =>
          form.setValue('comment', draft, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
          })
        }
      />
    </div>
  );
};

export { RevisionChatInputForm };
